import DOMPurify from "dompurify";

import { MailAddress, SupportMailRow } from "../../slices/SupportSlice";

/* Help desk "Emails" tab rules (owner 2026-10-26). */

export type MailFolder = "inbox" | "sent";

export const readFolder = (value: string | null): MailFolder =>
  value === "sent" ? "sent" : "inbox";

export const addressLabel = (a?: MailAddress | null) =>
  (a?.name || "").trim() || (a?.email || "").trim();

/** Inbox rows show the sender; Sent rows (and our own emails) show "To: …". */
export const mailWho = (row: SupportMailRow, folder: MailFolder) => {
  if (folder === "sent" || row.fromUs) {
    const to = row.to.map(addressLabel).filter(Boolean);
    if (!to.length) return "To: (no one)";
    return `To: ${to.slice(0, 2).join(", ")}${
      to.length > 2 ? ` +${to.length - 2}` : ""
    }`;
  }
  return addressLabel(row.from) || "(unknown sender)";
};

/** Today = time, this year = "Oct 3", older = "Oct 3, 2024". */
export const mailDate = (unix: number, now: Date = new Date()) => {
  if (!unix) return "";
  const d = new Date(unix * 1000);
  if (d.toDateString() === now.toDateString())
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
};

export const mailDateLong = (unix: number) =>
  unix
    ? new Date(unix * 1000).toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

/**
 * The email's HTML made safe to show: no scripts, forms or event handlers; links open in a new tab. It is shown
 * inside a sandboxed frame (no scripts), so its styles can't change the page around it.
 */
export const safeEmailHtml = (html: string) => {
  const clean = DOMPurify.sanitize(html || "", {
    FORBID_TAGS: [
      "script",
      "form",
      "input",
      "button",
      "textarea",
      "select",
      "iframe",
      "object",
      "embed",
      "base",
      "meta",
      "link",
    ],
    FORBID_ATTR: ["srcdoc", "formaction"],
    WHOLE_DOCUMENT: false,
  });
  const box = document.createElement("div");
  box.innerHTML = clean;
  box.querySelectorAll("a[href]").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  });
  return box.innerHTML;
};

export const emailFrameDocument = (html: string) =>
  `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;font-family:Inter,Arial,sans-serif;font-size:14px;line-height:1.55;color:#1F2433;word-wrap:break-word}img{max-width:100%;height:auto}a{color:#6B4EFF}blockquote{margin:0 0 0 8px;padding-left:10px;border-left:3px solid #DCE2EA;color:#5D6475}</style></head><body>${safeEmailHtml(
    html,
  )}</body></html>`;

/** The words on the case button: a new case, or the one this email already belongs to. */
export const caseButton = (row: Pick<SupportMailRow, "caseId" | "caseCode">) =>
  row.caseId
    ? { label: `Open case #${row.caseCode || ""}`.trim(), existing: true }
    : { label: "Create case", existing: false };

export const createdMessage = (r: {
  created: boolean;
  code: string;
  added: number;
}) =>
  r.created
    ? `Case #${r.code} created. No reply was sent.`
    : r.added > 0
    ? `Added ${r.added} email${r.added === 1 ? "" : "s"} to case #${r.code}.`
    : `This email is already in case #${r.code}.`;
